import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import { Badge } from "@/components/ui/badge";
import { Download, MoreHorizontal, Upload, Loader, FileSpreadsheet } from "lucide-react";
import { cn } from "@/lib/utils";
import { colors } from "@/utils/colors";
import { LeftoverProductProcess } from "@/types/leftoverProducts";
import { formatDate } from "@/utils/format-date";
import { ApiDataProps } from "@/pages/Tenants";
import { TypePageActions } from "@/pages/Plans";
import { ShowBtn } from "@/components/Show";
import {
  showLeftoverProductProcess,
  addFileToLeftoverProduct,
  exportLeftoverProductProcess,
} from "@/Api/leftoverProducts";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Button } from "@/components/ui/button";
import { useState } from "react";
import { toast } from "sonner";
import { Mask } from "@/utils/mask";

interface TableDataProps extends LeftoverProductProcess {}

export const TableData = ({
  dataApi,
  pageActions,
}: {
  dataApi: ApiDataProps<TableDataProps>;
  pageActions: TypePageActions;
}) => {
  const [refreshKey, setRefreshKey] = useState(0);
  const [uploadingFiles, setUploadingFiles] = useState<{
    [key: string]: boolean;
  }>({});
  const [exportingFiles, setExportingFiles] = useState<{
    [key: string]: boolean;
  }>({});

  const handleAddFile = async (uuid: string) => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = ".pdf,.doc,.docx,.jpg,.jpeg,.png";
    input.onchange = async (e: any) => {
      const file = e.target.files[0];
      if (!file) return;

      setUploadingFiles((prev) => ({ ...prev, [uuid]: true }));
      try {
        const formData = new FormData();
        formData.append("description", "Arquivo obrigatório das sobras");
        formData.append("files[]", file);

        const response = await addFileToLeftoverProduct(uuid, formData);
        if (response.ok) {
          toast.success("Arquivo adicionado com sucesso!");
          setRefreshKey((prev) => prev + 1);
        }
      } catch (error) {
        console.error("Erro ao adicionar arquivo:", error);
        toast.error("Erro ao adicionar arquivo");
      } finally {
        setUploadingFiles((prev) => ({ ...prev, [uuid]: false }));
      }
    };
    input.click();
  };

  const handleExport = async (uuid: string, lotteryName: string, contestName: string) => {
    setExportingFiles((prev) => ({ ...prev, [uuid]: true }));
    try {
      const blob = await exportLeftoverProductProcess(uuid);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const safeName = `${lotteryName}-${contestName}`.replace(/\s+/g, "-").toLowerCase();
      a.download = `sobras-${safeName}-${new Date().toISOString().split("T")[0]}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast.success("Exportado com sucesso!");
    } catch (error) {
      console.error("Erro ao exportar:", error);
      toast.error("Erro ao exportar sobras");
    } finally {
      setExportingFiles((prev) => ({ ...prev, [uuid]: false }));
    }
  };

  return (
    <>
      <Table className="bg-zinc-50 rounded dark:bg-[#2c2c2c]">
        <TableHeader>
          <TableRow>
            <TableHead>Concurso</TableHead>
            <TableHead>Loteria</TableHead>
            <TableHead>Unidade</TableHead>
            <TableHead>Quantidade</TableHead>
            <TableHead>Valor Sobra</TableHead>
            <TableHead>Status do concurso</TableHead>
            <TableHead>Criado por</TableHead>
            <TableHead>Data</TableHead>
            <TableHead className="text-center">Ações</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {dataApi?.data.map((item) => (
            <TableRow key={item?.id}>
              <TableCell>
                <div className="flex flex-col">
                  <span className="font-medium">{item?.contest?.name}</span>
                  <span className="text-sm text-muted-foreground">
                    {formatDate(item?.contest?.validation_date)}
                  </span>
                </div>
              </TableCell>
              <TableCell>
                <Badge
                  className="text-xs text-white border-0"
                  style={{ backgroundColor: item?.lottery?.color || "#000" }}
                >
                  {item?.lottery?.name}
                </Badge>
              </TableCell>
              <TableCell>{item?.unity?.name}</TableCell>
              <TableCell className="text-center">
                <Badge variant="outline" className="font-mono">
                  {item?.quota_quantity_total}
                </Badge>
              </TableCell>
              <TableCell>
                <span className="font-medium text-green-600 dark:text-green-400">
                  {Mask.formatCurrency(item?.total_value || 0)}
                </span>
              </TableCell>
              <TableCell>
                <Badge
                  className={cn(
                    "text-xs",
                    item?.contest?.contest_status_id === 4
                      ? "bg-red-100 text-red-800 border-red-300"
                      : "bg-green-100 text-green-800 border-green-300"
                  )}
                >
                  {item?.contest?.contest_status?.name}
                </Badge>
              </TableCell>
              <TableCell>{item?.created_by?.name}</TableCell>
              <TableCell>{formatDate(item?.created_at)}</TableCell>
              <TableCell className="flex justify-center">
                <div className="flex gap-3 w-full justify-center">
                  <ShowBtn
                    key={`${item?.uuid}-${refreshKey}`}
                    labels={{ title: "Visualizar Produto Sobra" }}
                    params={{
                      func: showLeftoverProductProcess,
                      funcParams: item?.uuid,
                    }}
                    render={(responseApi: TableDataProps) => {
                      const productsWithQuantity =
                        responseApi?.leftover_products?.filter(
                          (product) => Number(product?.quota_quantity) > 0
                        ) ?? [];

                      return (
                        <ScrollArea className="h-[500px] pr-4">
                          <div className="w-full h-auto">
                            <div className="flex flex-col gap-4">
                              {/* Botao de Exportar */}
                              <div className="flex justify-end">
                                <Button
                                  onClick={() =>
                                    handleExport(
                                      responseApi?.uuid,
                                      responseApi?.lottery?.name || "",
                                      responseApi?.contest?.name || ""
                                    )
                                  }
                                  size="sm"
                                  variant="outline"
                                  disabled={exportingFiles[responseApi?.uuid]}
                                  className="gap-2"
                                >
                                  {exportingFiles[responseApi?.uuid] ? (
                                    <Loader className="h-4 w-4 animate-spin" />
                                  ) : (
                                    <FileSpreadsheet className="h-4 w-4" />
                                  )}
                                  Exportar Excel
                                </Button>
                              </div>

                              {/* Header melhorado */}
                              <div className="bg-gradient-to-r from-slate-50 to-slate-100 dark:from-slate-800 dark:to-slate-900 rounded-lg p-4 border border-slate-200 dark:border-slate-700">
                                <div className="flex items-center gap-3 mb-4">
                                  <div
                                    className="px-3 py-1.5 rounded-lg text-white text-sm font-semibold shadow-sm"
                                    style={{ backgroundColor: responseApi?.lottery?.color || '#023859' }}
                                  >
                                    {responseApi?.lottery?.name}
                                  </div>
                                  <h3 className="text-lg font-bold text-slate-900 dark:text-white">
                                    {responseApi?.contest?.name}
                                  </h3>
                                  <span className="text-sm text-slate-500 dark:text-slate-400">|</span>
                                  <p className="text-sm text-slate-500 dark:text-slate-400">
                                    {responseApi?.unity?.name}
                                  </p>
                                </div>
                                <div className="flex gap-4 mb-3">
                                  <div className="text-center px-4 py-2 bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-600 shadow-sm flex-1">
                                    <p className="text-2xl font-bold text-slate-900 dark:text-white">
                                      {responseApi?.quota_quantity_total}
                                    </p>
                                    <p className="text-xs text-slate-500 dark:text-slate-400 uppercase tracking-wide">
                                      Cotas
                                    </p>
                                  </div>
                                  <div className="text-center px-4 py-2 bg-green-50 dark:bg-green-900/30 rounded-lg border border-green-200 dark:border-green-700 shadow-sm flex-1">
                                    <p className="text-2xl font-bold text-green-600 dark:text-green-400">
                                      {Mask.formatCurrency(responseApi?.total_value || 0)}
                                    </p>
                                    <p className="text-xs text-green-600 dark:text-green-400 uppercase tracking-wide">
                                      Valor Total
                                    </p>
                                  </div>
                                </div>
                                <div className="flex items-center justify-between pt-3 border-t border-slate-200 dark:border-slate-600">
                                  <p className="text-sm text-slate-600 dark:text-slate-400">
                                    <span className="font-medium">Criado por:</span> {responseApi?.created_by?.name}
                                  </p>
                                  <p className="text-sm text-slate-500 dark:text-slate-400">
                                    {formatDate(responseApi?.created_at)}
                                  </p>
                                </div>
                              </div>

                              {/* Card de Produtos */}
                              <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg p-4">
                                <div className="flex items-center gap-2 mb-3">
                                  <div className="p-2 bg-blue-100 dark:bg-blue-900/50 rounded-lg">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-blue-600 dark:text-blue-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                      <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"></path>
                                      <polyline points="3.27 6.96 12 12.01 20.73 6.96"></polyline>
                                      <line x1="12" y1="22.08" x2="12" y2="12"></line>
                                    </svg>
                                  </div>
                                  <span className="font-semibold text-slate-800 dark:text-slate-200">Produtos ({productsWithQuantity.length})</span>
                                </div>
                                {productsWithQuantity.length > 0 ? (
                                  <div className="space-y-2">
                                    {productsWithQuantity.map((product, index) => (
                                      <div
                                        key={index}
                                        className="p-3 bg-slate-50 dark:bg-slate-700/50 border border-slate-200 dark:border-slate-600 rounded-lg"
                                      >
                                        <div className="flex items-center justify-between">
                                          <div>
                                            <p className="font-medium text-slate-800 dark:text-slate-200">
                                              {product?.contest_product_classification?.product_name}
                                            </p>
                                            <p className="text-sm text-slate-500 dark:text-slate-400">
                                              {product?.contest_product_classification?.product_classification?.name}
                                            </p>
                                          </div>
                                          <Badge variant="secondary" className="font-mono bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300">
                                            {product?.quota_quantity} cotas
                                          </Badge>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                ) : (
                                  <p className="text-sm text-slate-500 dark:text-slate-400">
                                    Nenhum produto com quantidade disponível.
                                  </p>
                                )}
                              </div>

                              {/* Card de Descrição */}
                              {responseApi?.description && (
                                <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-lg p-4">
                                  <div className="flex items-start gap-3">
                                    <div className="p-2 bg-amber-100 dark:bg-amber-800/50 rounded-lg">
                                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-amber-600 dark:text-amber-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                                        <polyline points="14 2 14 8 20 8"></polyline>
                                        <line x1="16" y1="13" x2="8" y2="13"></line>
                                        <line x1="16" y1="17" x2="8" y2="17"></line>
                                        <polyline points="10 9 9 9 8 9"></polyline>
                                      </svg>
                                    </div>
                                    <div className="flex-1">
                                      <p className="text-sm font-semibold text-amber-800 dark:text-amber-300 mb-1">Descrição</p>
                                      <p className="text-sm text-amber-700 dark:text-amber-400">{responseApi?.description}</p>
                                    </div>
                                  </div>
                                </div>
                              )}

                              {/* Seção de Arquivos */}
                              <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg p-4">
                                <div className="flex items-center justify-between mb-3">
                                  <div className="flex items-center gap-2">
                                    <div className="p-2 bg-slate-100 dark:bg-slate-700 rounded-lg">
                                      <Upload className="h-4 w-4 text-slate-600 dark:text-slate-400" />
                                    </div>
                                    <span className="font-semibold text-slate-800 dark:text-slate-200">Arquivos</span>
                                  </div>
                                  <Button
                                    onClick={() =>
                                      handleAddFile(responseApi?.uuid)
                                    }
                                    size="sm"
                                    variant="outline"
                                    disabled={uploadingFiles[responseApi?.uuid]}
                                    className="flex gap-2"
                                  >
                                    <Upload className="size-4" />
                                    {uploadingFiles[responseApi?.uuid]
                                      ? "Enviando..."
                                      : "Adicionar arquivo"}
                                  </Button>
                                </div>

                                {/* File antigo (singular) */}
                                {responseApi?.file && (
                                  <div className="mb-3 p-3 border rounded-lg bg-yellow-50">
                                    <p className="text-xs text-yellow-700 mb-2">
                                      Arquivo antigo (modelo anterior)
                                    </p>
                                    <img
                                      src={responseApi?.file}
                                      alt="Arquivo do produto sobra"
                                      className="max-w-full max-h-48 object-contain rounded border"
                                    />
                                  </div>
                                )}

                                {/* Files novos (array) */}
                                {responseApi?.files &&
                                responseApi.files.length > 0 ? (
                                  <div className="space-y-2">
                                    {responseApi.files.map((file, index) => {
                                      // Helper: detect file type from url
                                      const url = file.file;
                                      let ext = "";
                                      try {
                                        ext =
                                          url
                                            ?.split("?")[0]
                                            ?.split(".")
                                            ?.pop()
                                            ?.toLowerCase() ?? "";
                                      } catch {
                                        ext = "";
                                      }
                                      console.log(ext);
                                      const isImage = [
                                        "jpg",
                                        "jpeg",
                                        "png",
                                        "gif",
                                        "webp",
                                        "bmp",
                                      ].includes(ext);
                                      const isPdf = ext === "pdf";
                                      const isDoc = ["doc", "docx"].includes(
                                        ext
                                      );

                                      // Helper: label
                                      let fileLabel = "";
                                      if (isImage) {
                                        fileLabel = "Imagem";
                                      } else if (isPdf) {
                                        fileLabel = "PDF";
                                      } else if (isDoc) {
                                        fileLabel = "Documento";
                                      } else {
                                        fileLabel = `Arquivo`;
                                      }

                                      return (
                                        <div
                                          key={file.id}
                                          className="p-3 border rounded-lg flex items-center gap-3"
                                        >
                                          {isImage ? (
                                            <img
                                              src={url}
                                              alt={`${fileLabel} ${index + 1}`}
                                              className="w-20 h-20 object-cover rounded"
                                            />
                                          ) : (
                                            <div className="w-20 h-20 flex items-center justify-center rounded bg-zinc-200 text-zinc-600 dark:bg-zinc-800 dark:text-zinc-300 border">
                                              {isPdf ? (
                                                <span className="text-xl font-bold">
                                                  PDF
                                                </span>
                                              ) : isDoc ? (
                                                <span className="text-xl font-bold">
                                                  DOC
                                                </span>
                                              ) : (
                                                <span className="text-xs text-center break-all">
                                                  ARQ
                                                </span>
                                              )}
                                            </div>
                                          )}
                                          <div className="flex-1">
                                            <p className="text-sm font-medium">
                                              {fileLabel} {index + 1}{" "}
                                              <span className="text-xs text-muted-foreground">
                                                (
                                                {isImage
                                                  ? ext.toUpperCase()
                                                  : isPdf
                                                  ? "PDF"
                                                  : isDoc
                                                  ? ext.toUpperCase()
                                                  : "Arquivo"}
                                                )
                                              </span>
                                            </p>
                                            <p className="text-xs text-gray-500">
                                              {formatDate(file.created_at)}
                                            </p>
                                          </div>
                                          <div className="flex gap-2">
                                            <Button
                                              onClick={() => {
                                                const link =
                                                  document.createElement("a");
                                                link.href = file.file;
                                                if (isImage) {
                                                  link.download = `imagem-${
                                                    index + 1
                                                  }.${ext}`;
                                                } else if (isPdf) {
                                                  link.download = `documento-${
                                                    index + 1
                                                  }.pdf`;
                                                } else if (isDoc) {
                                                  link.download = `documento-${
                                                    index + 1
                                                  }.${ext}`;
                                                } else {
                                                  link.download = `arquivo-${
                                                    index + 1
                                                  }`;
                                                }
                                                link.target = "_blank";
                                                link.click();
                                              }}
                                              variant="outline"
                                              size="icon"
                                              className="size-8"
                                              title={`Baixar ${fileLabel}`}
                                            >
                                              <Download className="size-4" />
                                            </Button>
                                          </div>
                                        </div>
                                      );
                                    })}
                                  </div>
                                ) : (
                                  !responseApi?.file && (
                                    <p className="text-sm text-gray-500">
                                      Nenhum arquivo adicionado
                                    </p>
                                  )
                                )}
                              </div>

                            </div>
                          </div>
                        </ScrollArea>
                      );
                    }}
                  />

                  {/* {(item?.file || (item?.files && item.files.length > 0)) && (
                    <Button
                      onClick={() => {
                        const fileToDownload =
                          item.files && item.files.length > 0
                            ? item.files[0].file
                            : item.file;
                        const link = document.createElement("a");
                        link.href = fileToDownload;
                        link.download = `arquivo-${
                          item?.contest?.name || "sobra"
                        }`;
                        link.target = "_blank";
                        link.click();
                      }}
                      variant="outline"
                      size="icon"
                      className="size-7 p-0"
                      title={
                        item.files && item.files.length > 1
                          ? `${item.files.length} arquivos`
                          : "Download arquivo"
                      }
                    >
                      <Download className="size-5" />
                    </Button>
                  )} */}
                </div>
              </TableCell>
            </TableRow>
          ))}
          {dataApi?.data.length === 0 && (
            <TableRow>
              <TableCell colSpan={9} className="text-center py-8">
                Nenhum produto sobra encontrado
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>

      <div className="flex justify-between mb-3">
        {dataApi?.data.length > 0 && (
          <span className="font-medium">
            Exibindo {dataApi?.current_page} de um total de {dataApi?.last_page}
          </span>
        )}
        {dataApi?.last_page > 1 && (
          <Pagination className="">
            <PaginationContent>
              <PaginationItem>
                <PaginationPrevious
                  href="#"
                  className={cn("h-8", colors.bg_primary)}
                  onClick={pageActions.previous}
                />
              </PaginationItem>
              <PaginationItem>
                <PaginationLink
                  href="#"
                  className={cn(
                    `size-8 bg-transparent border-2 border-[#023859] text-slate-900 font-bold cursor-auto hover:bg-transparent`
                  )}
                >
                  <span className="pointer-events-none dark:text-[#F4F4F5]">
                    {dataApi?.current_page}
                  </span>
                </PaginationLink>
              </PaginationItem>
              <PaginationItem>
                <PaginationLink
                  href="#"
                  className="text-black h-8 hover:bg-stone-950/10"
                  onClick={pageActions.lastPage}
                >
                  <MoreHorizontal className="h-4 w-4 dark:text-[#F4F4F5]" />
                </PaginationLink>
              </PaginationItem>
              <PaginationItem>
                <PaginationNext
                  href="#"
                  className={cn("h-8", colors.bg_primary)}
                  onClick={pageActions.next}
                />
              </PaginationItem>
            </PaginationContent>
          </Pagination>
        )}
      </div>
    </>
  );
};
